import { getMultisigBalance } from 'api';
import useGetTokenMetadata from 'hooks/use-getTokenMetadata';
import useNetwork from 'hooks/use-network';
import { SafeFtBalance, useTokenPrices } from 'store';
import { IMultiSignWallet, StxChainEnum } from 'types';

export const useGetSafeBalance = () => {
  const [network] = useNetwork();

  const { tokenPrices: tokenPriceRecord } = useTokenPrices();
  const { fetchTokenMetadata } = useGetTokenMetadata();

  const getSafeBalance = async (safe: IMultiSignWallet): Promise<number> => {
    const safeBalances = await getMultisigBalance(safe.address, StxChainEnum[network], {
      includeNfts: false,
    });
    const fungible_tokens = safeBalances.result?.fungible_tokens || [];
    const safeStxBalance = safeBalances.result?.stx;

    const stxBalance: SafeFtBalance = {
      asset: {
        address: 'STX',
        symbol: 'STX',
        decimals: 6,
        ref: 'STX',
      },
      balance: (Number(safeStxBalance.balance) - Number(safeStxBalance.locked)).toString(),
    };

    // Collect all fungible token balances from api response
    const ftBalancesApi: SafeFtBalance[] = await Promise.all(
      fungible_tokens.map((ftToken) => {
        const [fullTokenAddress, tokenRef] = ftToken.token.split('::');

        return fetchTokenMetadata(fullTokenAddress, tokenRef);
      })
    ).then((metadataResponses) => {
      return metadataResponses.map(
        ({ address, symbol, decimals, ref, metadata }): SafeFtBalance => ({
          asset: {
            address,
            symbol,
            decimals,
            ref,
            metadata,
          },
          balance: fungible_tokens.find((x) => x.contract === address)?.balance || '',
        })
      );
    });

    // Merge all fungible tokens. Append default token list in the end.
    const ftBalances: SafeFtBalance[] = [stxBalance, ...ftBalancesApi];

    return ftBalances.reduce((acc, balanceItem) => {
      const price = tokenPriceRecord[balanceItem.asset.address];
      if (!price) return acc;

      return acc + price * (Number(balanceItem.balance) / Math.pow(10, balanceItem.asset.decimals));
    }, 0);
  };

  return getSafeBalance;
};
